import React, { useState, useEffect, useCallback } from "react";
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Button, Input, Alert, Spinner,
} from "reactstrap";
import axios from "axios";
import { API_URL_product_types } from "../../constants";
import { getAuthHeaders } from "../../utils/auth";
import { IconTrash, IconPlus } from "../ui/icons";

// Minimal admin surface for the Producto Tipo catalog:
// - Lists existing types
// - Add a new one (name only; orden defaults to 100 server-side)
// - Delete an existing one (no confirmation modal — Producto.tipo is a
//   free-text string, so removing a catalog entry just hides it from the
//   dropdown; existing Productos keep their string value)
// On close, calls onSaved so the parent refetches its own tipo list.

export default function ProductTypeManagerModal({ isOpen, toggle, onSaved }) {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newName, setNewName] = useState("");
  const [error, setError] = useState("");
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(API_URL_product_types, { headers: getAuthHeaders() });
      setTypes(res.data || []);
    } catch (e) {
      setError("No se pudieron cargar los tipos.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    if (types.some((t) => t.nombre.toLowerCase() === name.toLowerCase())) {
      setError(`"${name}" ya existe en la lista.`);
      return;
    }
    setSaving(true);
    setError("");
    try {
      await axios.post(
        API_URL_product_types,
        { nombre: name, orden: 100 },
        { headers: getAuthHeaders() }
      );
      setNewName("");
      setDirty(true);
      await load();
    } catch (e) {
      setError(e.response?.data?.nombre?.[0] || "No se pudo crear el tipo.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Eliminar el tipo "${t.nombre}"?\n\nLos productos existentes con este tipo conservan su valor; sólo se remueve del desplegable.`)) return;
    setError("");
    try {
      await axios.delete(`${API_URL_product_types}${t.pk}/`, { headers: getAuthHeaders() });
      setDirty(true);
      await load();
    } catch (e) {
      setError(`No se pudo eliminar "${t.nombre}".`);
    }
  };

  const handleClose = () => {
    if (dirty && onSaved) onSaved();
    setDirty(false);
    setNewName("");
    setError("");
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered>
      <ModalHeader toggle={handleClose}>Administrar tipos de producto</ModalHeader>
      <ModalBody>
        {error && <Alert color="danger">{error}</Alert>}
        {loading ? (
          <div className="text-center py-4"><Spinner size="sm" /></div>
        ) : (
          <div>
            <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 8, marginBottom: 12 }}>
              {types.length === 0 ? (
                <div style={{ padding: "16px 12px", color: "#6b7280", fontSize: "0.875rem", textAlign: "center" }}>
                  No hay tipos definidos.
                </div>
              ) : (
                types.map((t) => (
                  <div
                    key={t.pk}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "8px 12px",
                      borderBottom: "1px solid #f3f4f6",
                    }}
                  >
                    <span>{t.nombre}</span>
                    <Button color="danger" size="sm" onClick={() => handleDelete(t)} title="Eliminar tipo">
                      <IconTrash />
                    </Button>
                  </div>
                ))
              )}
            </div>
            <form onSubmit={handleAdd} style={{ display: "flex", gap: 8 }}>
              <Input
                type="text"
                placeholder="Nombre del nuevo tipo"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                disabled={saving}
                maxLength={100}
              />
              <Button color="primary" type="submit" disabled={saving || !newName.trim()}>
                {saving ? <Spinner size="sm" /> : <><IconPlus /> Agregar</>}
              </Button>
            </form>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose}>Cerrar</Button>
      </ModalFooter>
    </Modal>
  );
}
